import type { EChartsOption } from 'echarts';

import toBulletOption from './bullet';
import toCartesianOption, { AxisLabelLayout } from './cartesian';
import toGaugeOption from './gauge';
import toHeatmapOption from './heatmap';
import toPartsOption from './parts';
import toPolarOption from './polar';
import toRegionMapOption from './regionMap';
import toSankeyOption from './sankey';
import toScatterOption from './scatter';
import toWaterfallOption from './waterfall';
import { FormatMetric } from './shared';

// The ECharts options a chart model draws with, by its kind
const toEChartsOption = ({
  model,
  theme,
  formatMetric,
  labelLayout,
  tickCount,
  size,
}: {
  model: StudioCharts.Model;
  theme: StudioCharts.Theme;
  formatMetric: FormatMetric;
  // How a cartesian chart's category labels fit (see getAxisLabelLayout);
  // absent as the model has them
  labelLayout?: AxisLabelLayout;
  // A cartesian chart's value axis intervals (see getValueTickCount)
  tickCount?: number;
  // The room the chart has, in pixels: what packed bubbles are packed into,
  // a gauge's arc sized by, and a bullet's text
  size?: { width: number; height: number };
}): EChartsOption => {
  switch (model.kind) {
    case 'pie':
    case 'funnel':
    case 'treemap':
    case 'packedBubble':
      return toPartsOption({ model, theme, formatMetric, size });
    case 'polar':
      return toPolarOption({ model, theme, formatMetric });
    case 'scatter':
      return toScatterOption({ model, theme, formatMetric });
    case 'heatmap':
      return toHeatmapOption({ model, theme, formatMetric });
    case 'waterfall':
      return toWaterfallOption({ model, theme, formatMetric });
    case 'sankey':
      return toSankeyOption({ model, theme, formatMetric });
    case 'gauge':
      return toGaugeOption({ model, theme, size });
    case 'regionMap':
      return toRegionMapOption({ model, theme, formatMetric });
    case 'bullet':
      return toBulletOption({ model, theme, formatMetric, size });
    case 'cartesian':
    default:
      return toCartesianOption({
        model,
        theme,
        formatMetric,
        labelLayout,
        tickCount,
      });
  }
};

export {
  getAxisLabelLayout,
  getValueTickCount,
  shouldRotateLabels,
  toMinRangeMax,
} from './cartesian';
export type { AxisLabelLayout } from './cartesian';
export { formatAxisValue } from './shared';
export default toEChartsOption;
